import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Divider,
  Empty,
  Input,
  List,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tabs,
  Typography,
  message
} from 'antd';
import {
  CheckOutlined,
  DownloadOutlined,
  EditOutlined,
  FileSearchOutlined,
  LinkOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import {
  answerTenderQuestion,
  approveTenderRevision,
  createEditableTenderRevisionCopy,
  decideTenderKnowledge,
  downloadTenderApprovedRevisionExcel,
  fetchTenderWorkspace,
  fillTenderMissingCoverageNegative,
  queueTenderWorkspace,
  updateTenderField
} from './api';
import type { TenderAutofillMode, TenderRevisionField, TenderWorkspaceResponse } from './types';

const { Paragraph, Text, Title } = Typography;

type TenderWorkspaceProps = {
  leadId: string;
  canManage: boolean;
  analyzedPolicyCount: number;
};

const statusLabels: Record<string, string> = {
  DRAFT: 'Kladde',
  PROCESSING: 'Behandler',
  REVIEW: 'Til gennemgang',
  APPROVED: 'Godkendt',
  FAILED: 'Fejlet',
  PENDING: 'Venter',
  RUNNING: 'Behandler',
  COMPLETED: 'Færdig'
};

const statusColors: Record<string, string> = {
  PROCESSING: 'processing',
  RUNNING: 'processing',
  PENDING: 'default',
  REVIEW: 'warning',
  APPROVED: 'success',
  COMPLETED: 'success',
  FAILED: 'error'
};

const autofillModeOptions: Array<{ value: TenderAutofillMode; label: string; description: string }> = [
  {
    value: 'STANDARD',
    label: 'Standard (som i dag)',
    description: 'Kun tydelige ikke-dækninger autoudfyldes; resten kræver afklaring.'
  }
];

function displayValue(value: unknown): string {
  if (value === null || value === undefined) {
    return 'Ikke udfyldt';
  }
  if (typeof value === 'object' && !Array.isArray(value)) {
    const row = value as Record<string, unknown>;
    if (Object.prototype.hasOwnProperty.call(row, 'value')) {
      return [row.value, row.currency]
        .filter((item) => item !== null && item !== undefined && item !== '')
        .join(' ');
    }
    return JSON.stringify(value);
  }
  return String(value);
}

function isCompleteField(field: TenderRevisionField): boolean {
  const value = field.value;
  const hasValue = value !== null
    && value !== undefined
    && !(typeof value === 'string' && !value.trim());
  return hasValue && field.review_status !== 'NEEDS_REVIEW';
}

function answerGuidance(field: TenderRevisionField | undefined): string | null {
  if (!field) {
    return null;
  }
  const context = field.metadata?.template_context?.toLowerCase() || '';
  const label = (field.metadata?.template_label || field.label).toLowerCase();
  if (context.includes('solceller') && label.includes('placering')) {
    return 'Angiv kun placeringen, fx Tag, jord eller facade. Skriv Ingen solceller, hvis de ikke er omfattet.';
  }
  return `Angiv kun den værdi, der skal stå i feltet ${field.metadata?.template_label || field.label}.`;
}

function clarificationTitle(question: string, field: TenderRevisionField | undefined): string {
  const context = field?.metadata?.template_context?.toLowerCase() || '';
  const label = (field?.metadata?.template_label || field?.label || '').toLowerCase();
  return context.includes('solceller') && label.includes('placering')
    ? 'Hvor er solcellerne placeret?'
    : question;
}

export default function TenderWorkspace({ leadId, canManage, analyzedPolicyCount }: TenderWorkspaceProps) {
  const [data, setData] = useState<TenderWorkspaceResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [knowledgeSelections, setKnowledgeSelections] = useState<Record<string, boolean>>({});
  const [editingField, setEditingField] = useState<TenderRevisionField | null>(null);
  const [fieldDraft, setFieldDraft] = useState('');
  const [sourceField, setSourceField] = useState<TenderRevisionField | null>(null);
  const [onlyIncomplete, setOnlyIncomplete] = useState(false);
  const [autofillMode, setAutofillMode] = useState<TenderAutofillMode>('STANDARD');

  const load = useCallback(async (silent = false) => {
    if (!silent) {
      setLoading(true);
    }
    try {
      const response = await fetchTenderWorkspace(leadId);
      setData(response);
      if (!silent) {
        const mode = response.workspace?.autofill_mode
          || response.latest_job?.metadata?.autofill_mode
          || 'STANDARD';
        setAutofillMode(mode);
      }
    } catch (error: any) {
      if (!silent) {
        message.error(error?.message || 'Kunne ikke hente udbudsværksted');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [leadId]);

  useEffect(() => {
    void load();
  }, [load]);

  const isProcessing = data?.workspace?.status === 'PROCESSING'
    || ['PENDING', 'RUNNING'].includes(data?.latest_job?.status || '');

  useEffect(() => {
    if (!isProcessing) {
      return undefined;
    }
    const interval = window.setInterval(() => void load(true), 2500);
    return () => window.clearInterval(interval);
  }, [isProcessing, load]);

  const openQuestions = useMemo(
    () => (data?.questions || []).filter((question) => question.is_required && question.status !== 'ANSWERED'),
    [data?.questions]
  );

  const automaticallyFilledFields = useMemo(
    () => (data?.fields || []).filter((field) => field.metadata?.auto_filled),
    [data?.fields]
  );

  const fieldGroups = useMemo(() => {
    const groups = new Map<string, TenderRevisionField[]>();
    for (const field of (data?.fields || []).filter((item) => !onlyIncomplete || !isCompleteField(item))) {
      const sheet = field.metadata?.template_sheet || 'Øvrige';
      const current = groups.get(sheet) || [];
      current.push(field);
      groups.set(sheet, current);
    }
    return [...groups.entries()].map(([sheet, fields]) => ({ sheet, fields }));
  }, [data?.fields, onlyIncomplete]);

  const fieldCompleteness = useMemo(() => {
    const fields = data?.fields || [];
    const complete = fields.filter(isCompleteField).length;
    return { total: fields.length, complete, incomplete: fields.length - complete };
  }, [data?.fields]);

  const allowedValuesByFieldId = useMemo(() => {
    const values: Record<string, string[]> = {};
    for (const field of data?.fields || []) {
      values[field.id] = Array.isArray(field.metadata?.allowed_values) ? field.metadata.allowed_values : [];
    }
    return values;
  }, [data?.fields]);

  const fieldsById = useMemo(
    () => new Map((data?.fields || []).map((field) => [field.id, field])),
    [data?.fields]
  );

  const runAction = async (action: () => Promise<void>, success: string) => {
    setActionLoading(true);
    try {
      await action();
      message.success(success);
      await load(true);
      return true;
    } catch (error: any) {
      message.error(error?.message || 'Handlingen kunne ikke gennemføres');
      return false;
    } finally {
      setActionLoading(false);
    }
  };

  const saveField = async () => {
    if (!editingField) {
      return;
    }
    let nextValue: unknown = fieldDraft;
    if (editingField.value && typeof editingField.value === 'object' && !Array.isArray(editingField.value)) {
      const current = editingField.value as Record<string, unknown>;
      if (Object.prototype.hasOwnProperty.call(current, 'value')) {
        nextValue = { ...current, value: fieldDraft };
      }
    }
    const saved = await runAction(() => updateTenderField(leadId, editingField.id, nextValue), 'Feltet er opdateret');
    if (saved) {
      setEditingField(null);
    }
  };

  const saveAutomaticField = async (field: TenderRevisionField) => {
    const draftKey = `auto:${field.id}`;
    const saved = await runAction(
      () => updateTenderField(leadId, field.id, answers[draftKey]),
      'Den automatiske værdi er overskrevet'
    );
    if (!saved) {
      return;
    }
    setAnswers((current) => {
      const next = { ...current };
      delete next[draftKey];
      return next;
    });
  };

  const fillMissingCoverageNegative = async () => {
    setActionLoading(true);
    try {
      const result = await fillTenderMissingCoverageNegative(leadId);
      message.success(
        result.updated_fields
          ? `${result.updated_fields} felter er autoudfyldt fra kontrollerede policedata`
          : 'Ingen ikke-redigerede felter med kontrollerede policedata blev fundet'
      );
      await load(true);
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke autoudfylde felter uden dækning');
    } finally {
      setActionLoading(false);
    }
  };

  const downloadApprovedExcel = async () => {
    setExportLoading(true);
    try {
      const blob = await downloadTenderApprovedRevisionExcel(leadId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `udbudsmateriale-revision-${data?.workspace?.revision_number || 'godkendt'}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      message.error(error?.message || 'Kunne ikke generere udbudsmateriale');
    } finally {
      setExportLoading(false);
    }
  };

  const workspace = data?.workspace;
  const currentRevisionId = workspace?.current_revision_id;
  const estimatedAiCost = workspace?.ai_usage?.estimatedCostUsd;

  return (
    <Card
      title="Udbudsværksted"
      loading={loading}
      extra={workspace ? (
        <Space size={8} wrap>
          {workspace.revision_number ? <Tag>Revision {workspace.revision_number}</Tag> : null}
          <Tag color={statusColors[workspace.status] || 'default'}>
            {statusLabels[workspace.status] || workspace.status}
          </Tag>
          {typeof estimatedAiCost === 'number' ? (
            <Tag color="cyan">
              AI ${estimatedAiCost.toFixed(2)} · {workspace.ai_usage?.requestCount || 0} kald
            </Tag>
          ) : null}
        </Space>
      ) : undefined}
    >
      {!workspace ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Intet udbudsmateriale oprettet">
          {canManage ? (
            <Space direction="vertical" size={8} style={{ width: '100%', maxWidth: 480 }}>
              <Text strong>Autoudfyldningsstrategi</Text>
              <Select
                value={autofillMode}
                onChange={(value) => setAutofillMode(value)}
                options={autofillModeOptions.map((option) => ({ value: option.value, label: option.label }))}
              />
              <Text type="secondary">
                {autofillModeOptions.find((option) => option.value === autofillMode)?.description}
              </Text>
              <Button
                type="primary"
                icon={<FileSearchOutlined />}
                loading={actionLoading}
                disabled={analyzedPolicyCount === 0}
                onClick={() => runAction(() => queueTenderWorkspace(leadId, autofillMode), 'Udbudsbehandling er startet')}
              >
                Start udbudsbehandling
              </Button>
            </Space>
          ) : null}
        </Empty>
      ) : (
        <Space direction="vertical" size={20} style={{ width: '100%' }}>
          {workspace.processing_error ? <Alert type="error" showIcon message={workspace.processing_error} /> : null}
          {isProcessing ? (
            <Alert
              type="info"
              showIcon
              message={`Dokumenter behandles${data?.latest_job ? ` · ${data.latest_job.progress}%` : ''}`}
            />
          ) : null}

          <Space size={8} wrap>
            {canManage ? (
              <Space size={8} wrap>
                <Text strong>Autoudfyldning:</Text>
                <Select
                  value={autofillMode}
                  style={{ minWidth: 320 }}
                  onChange={(value) => setAutofillMode(value)}
                  options={autofillModeOptions.map((option) => ({ value: option.value, label: option.label }))}
                  disabled={isProcessing}
                />
              </Space>
            ) : null}
            {canManage && workspace.latest_approved_revision_id ? (
              <Button
                icon={<EditOutlined />}
                loading={actionLoading || isProcessing}
                disabled={isProcessing}
                onClick={() => runAction(
                  () => createEditableTenderRevisionCopy(leadId),
                  'Redigerbar kopi af den godkendte revision er oprettet'
                )}
              >
                Redigér godkendt revision
              </Button>
            ) : null}
            {canManage ? (
              <Button
                icon={<ReloadOutlined />}
                loading={actionLoading || isProcessing}
                disabled={isProcessing || analyzedPolicyCount === 0}
                onClick={() => runAction(() => queueTenderWorkspace(leadId, autofillMode), 'Ny revision er sat i kø')}
              >
                Genopbyg revision
              </Button>
            ) : null}
            {canManage && currentRevisionId ? (
              <Button
                type="primary"
                loading={actionLoading || isProcessing}
                disabled={isProcessing || workspace.revision_status === 'APPROVED'}
                onClick={() => void fillMissingCoverageNegative()}
              >
                Udtræk kontrolleret, autoudfyld resten
              </Button>
            ) : null}
            {canManage && currentRevisionId ? (
              <Button
                type="primary"
                icon={<CheckOutlined />}
                loading={actionLoading}
                disabled={workspace.revision_status === 'APPROVED'
                  || openQuestions.length > 0
                  || fieldCompleteness.incomplete > 0
                  || isProcessing}
                onClick={() => runAction(
                  () => approveTenderRevision(leadId, currentRevisionId),
                  'Udbudsrevisionen er godkendt'
                )}
              >
                Godkend revision
              </Button>
            ) : null}
            <Button
              icon={<DownloadOutlined />}
              loading={exportLoading}
              disabled={workspace.revision_status !== 'APPROVED' || exportLoading}
              onClick={downloadApprovedExcel}
            >
              Eksportér udbudsmateriale
            </Button>
            {openQuestions.length ? <Tag color="warning">{openQuestions.length} obligatoriske afklaringer</Tag> : null}
            <Tag color="blue">
              Mode: {autofillModeOptions.find((option) => option.value === (workspace.autofill_mode || autofillMode))?.label}
            </Tag>
            <Tag color={fieldCompleteness.incomplete ? 'warning' : 'success'}>
              {fieldCompleteness.complete}/{fieldCompleteness.total} felter klar
            </Tag>
          </Space>

          <Alert
            type="info"
            showIcon
            message={autofillModeOptions.find((option) => option.value === autofillMode)?.description}
          />

          {fieldCompleteness.incomplete ? (
            <Alert
              type="warning"
              showIcon
              message={`${fieldCompleteness.incomplete} felter mangler værdi eller gennemgang før godkendelse`}
            />
          ) : null}

          <section>
            <Space size={12} wrap style={{ marginBottom: 8 }}>
              <Title level={5} style={{ margin: 0 }}>Udbudsfelter</Title>
              <Checkbox checked={onlyIncomplete} onChange={(event) => setOnlyIncomplete(event.target.checked)}>
                Vis kun mangler
              </Checkbox>
            </Space>
            <Tabs
              items={fieldGroups.map(({ sheet, fields }) => ({
                key: sheet,
                label: `${sheet} (${fields.length})`,
                children: <Table<TenderRevisionField>
                  rowKey="id"
                  size="small"
                  pagination={{ pageSize: 25, showSizeChanger: false, hideOnSinglePage: true }}
                  dataSource={fields}
                  scroll={{ x: 900 }}
                  columns={[
                {
                  title: 'Felt',
                  dataIndex: 'label',
                  key: 'label',
                  width: 360,
                  render: (_, field) => (
                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                      <Space size={6} wrap>
                        <Tag>{field.metadata?.template_sheet ? `Ark: ${field.metadata.template_sheet}` : 'Ark ikke angivet'}</Tag>
                        {field.metadata?.template_section ? <Tag color="geekblue">Sektion: {field.metadata.template_section}</Tag> : null}
                        {field.metadata?.template_context ? <Text strong>{field.metadata.template_context}</Text> : null}
                      </Space>
                      <Text>{field.metadata?.template_label || field.label}</Text>
                      {field.metadata?.template_cell ? <Text type="secondary">Felt: {field.metadata.template_cell}</Text> : null}
                      {field.metadata?.reason ? <Text type="secondary">{field.metadata.reason}</Text> : null}
                    </Space>
                  )
                },
                { title: 'Værdi', key: 'value', render: (_, field) => displayValue(field.value) },
                {
                  title: 'Status',
                  key: 'status',
                  width: 150,
                  render: (_, field) => (
                    <Tag color={field.review_status === 'NEEDS_REVIEW' ? 'warning' : field.review_status === 'CONFIRMED' ? 'success' : 'default'}>
                      {field.review_status === 'NEEDS_REVIEW'
                        ? 'Kræver gennemgang'
                        : field.review_status === 'CONFIRMED' ? 'Bekræftet' : 'Udtrukket'}
                    </Tag>
                  )
                },
                {
                  title: 'Handlinger',
                  key: 'actions',
                  width: 190,
                  render: (_, field) => (
                    <Space size={4}>
                      <Button
                        type="text"
                        icon={<LinkOutlined />}
                        disabled={!field.sources.length}
                        onClick={() => setSourceField(field)}
                      >
                        Kilder
                      </Button>
                      {canManage && workspace.revision_status !== 'APPROVED' ? (
                        <Button
                          type="text"
                          icon={<EditOutlined />}
                          onClick={() => {
                            setEditingField(field);
                            const current = displayValue(field.value);
                            setFieldDraft(current === 'Ikke udfyldt' ? '' : current);
                          }}
                        >
                          Redigér
                        </Button>
                      ) : null}
                    </Space>
                  )
                }
                  ]}
                />
              }))}
            />
          </section>

          <Divider style={{ margin: 0 }} />

          <section>
            <Title level={5}>Afklaringer</Title>
            {automaticallyFilledFields.length ? (
              <List
                dataSource={automaticallyFilledFields}
                renderItem={(field) => {
                  const draftKey = `auto:${field.id}`;
                  const currentValue = displayValue(field.value);
                  const draftValue = answers[draftKey] ?? currentValue;
                  const allowedValues = field.metadata?.allowed_values || [];
                  const hasChanged = draftValue.trim() !== currentValue.trim();
                  return <List.Item key={draftKey}>
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                      <Space size={8} wrap>
                        <Text strong>{field.metadata?.template_context || field.metadata?.template_label || field.label}</Text>
                        <Tag color="blue">Automatisk udfyldt</Tag>
                        <Tag color="success">Intet svar påkrævet</Tag>
                      </Space>
                      <Text type="secondary">{field.metadata?.reason}</Text>
                      <Alert
                        type="info"
                        showIcon
                        message={[
                          field.metadata?.template_sheet ? `Ark: ${field.metadata.template_sheet}` : null,
                          field.metadata?.template_section,
                          field.metadata?.template_context,
                          field.metadata?.template_label || field.label,
                          field.metadata?.template_cell ? `Felt ${field.metadata.template_cell}` : null
                        ].filter(Boolean).join(' → ')}
                        description={`Systemet har gemt værdien ${currentValue}. Ret kun værdien, hvis den skal overskrives.`}
                      />
                      {data?.workspace?.revision_status === 'APPROVED' ? (
                        <Alert
                          type="warning"
                          showIcon
                          message="Den godkendte revision er låst"
                          description="Brug “Redigér godkendt revision” ovenfor for at oprette en redigerbar kopi."
                        />
                      ) : allowedValues.length ? (
                        <Select
                          value={draftValue}
                          style={{ width: '100%', maxWidth: 420 }}
                          options={allowedValues.map((value) => ({ value, label: value }))}
                          onChange={(value) => setAnswers((current) => ({ ...current, [draftKey]: value }))}
                        />
                      ) : (
                        <Input.TextArea
                          value={draftValue}
                          autoSize={{ minRows: 2, maxRows: 5 }}
                          onChange={(event) => setAnswers((current) => ({ ...current, [draftKey]: event.target.value }))}
                        />
                      )}
                      {data?.workspace?.revision_status !== 'APPROVED' ? (
                        <Button
                          type="primary"
                          disabled={!hasChanged || !draftValue.trim()}
                          loading={actionLoading}
                          onClick={() => saveAutomaticField(field)}
                        >
                          Gem ændring
                        </Button>
                      ) : null}
                    </Space>
                  </List.Item>;
                }}
              />
            ) : null}
            <List
              dataSource={data?.questions || []}
              locale={{ emptyText: 'Ingen afklaringer' }}
              renderItem={(question) => {
                const field = question.field_id ? fieldsById.get(question.field_id) : undefined;
                const allowedValues = allowedValuesByFieldId[question.field_id || ''] || [];
                const guidance = answerGuidance(field);
                return <List.Item key={question.id}>
                  <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    <Space size={8} wrap>
                      <Text strong>{clarificationTitle(question.question, field)}</Text>
                      {question.is_required ? <Tag color="red">Obligatorisk</Tag> : null}
                      <Tag color={question.status === 'ANSWERED' ? 'success' : 'warning'}>
                        {question.status === 'ANSWERED' ? 'Besvaret' : 'Åben'}
                      </Tag>
                    </Space>
                    <Text type="secondary">{question.reason}</Text>
                    {field ? (
                      <Alert
                        type="info"
                        showIcon
                        message={[
                          field.metadata?.template_sheet ? `Ark: ${field.metadata.template_sheet}` : null,
                          field.metadata?.template_section,
                          field.metadata?.template_context,
                          field.metadata?.template_label || field.label,
                          field.metadata?.template_cell ? `Felt ${field.metadata.template_cell}` : null
                        ].filter(Boolean).join(' → ')}
                        description={allowedValues.length
                          ? 'Vælg én af de tilladte værdier nedenfor. Svaret skrives direkte i feltet.'
                          : guidance}
                      />
                    ) : null}
                    {question.evidence ? <Paragraph code style={{ marginBottom: 0 }}>{question.evidence}</Paragraph> : null}
                    {question.status === 'ANSWERED' ? (
                      <Text>{question.answer_text}</Text>
                    ) : canManage && workspace.revision_status !== 'APPROVED' ? (
                      <>
                        {allowedValues.length ? (
                          <Select
                            value={answers[question.id] || undefined}
                            placeholder={`Vælg ${field?.metadata?.template_label || field?.label || 'svar'}`}
                            style={{ width: '100%', maxWidth: 420 }}
                            options={allowedValues.map((value) => ({ value, label: value }))}
                            onChange={(value) => setAnswers((current) => ({ ...current, [question.id]: value }))}
                          />
                        ) : (
                          <Input.TextArea
                            value={answers[question.id] || ''}
                            placeholder={field ? `Skriv ${field.metadata?.template_label || field.label}` : 'Skriv svar'}
                            autoSize={{ minRows: 2, maxRows: 5 }}
                            onChange={(event) => setAnswers((current) => ({ ...current, [question.id]: event.target.value }))}
                          />
                        )}
                        <Space size={12} wrap>
                          <Checkbox
                            checked={Boolean(knowledgeSelections[question.id])}
                            onChange={(event) => setKnowledgeSelections((current) => ({
                              ...current,
                              [question.id]: event.target.checked
                            }))}
                          >
                            Indsend til organisationsviden
                          </Checkbox>
                          <Button
                            type="primary"
                            disabled={!answers[question.id]?.trim()}
                            loading={actionLoading}
                            onClick={() => runAction(
                              () => answerTenderQuestion(
                                leadId,
                                question.id,
                                answers[question.id],
                                Boolean(knowledgeSelections[question.id])
                              ),
                              'Afklaringen er gemt'
                            )}
                          >
                            Gem svar
                          </Button>
                        </Space>
                      </>
                    ) : null}
                  </Space>
                </List.Item>;
              }}
            />
          </section>

          {(data?.knowledge_entries || []).length ? (
            <>
              <Divider style={{ margin: 0 }} />
              <section>
                <Title level={5}>Organisationsviden til review</Title>
                <List
                  dataSource={data?.knowledge_entries || []}
                  renderItem={(entry) => (
                    <List.Item key={entry.id}>
                      <Space direction="vertical" size={8} style={{ width: '100%' }}>
                        <Space size={8} wrap>
                          <Text strong>{entry.title}</Text>
                          <Tag color={entry.status === 'APPROVED' ? 'success' : entry.status === 'REJECTED' ? 'error' : 'warning'}>
                            {entry.status === 'APPROVED' ? 'Godkendt' : entry.status === 'REJECTED' ? 'Afvist' : 'Afventer review'}
                          </Tag>
                        </Space>
                        <Paragraph style={{ marginBottom: 0 }}>{entry.content}</Paragraph>
                        {canManage && entry.status === 'PENDING' ? (
                          <Space size={8}>
                            <Button
                              type="primary"
                              loading={actionLoading}
                              onClick={() => runAction(
                                () => decideTenderKnowledge(leadId, entry.id, 'APPROVED'),
                                'Vidensforslaget er godkendt'
                              )}
                            >
                              Godkend som organisationsviden
                            </Button>
                            <Button
                              danger
                              loading={actionLoading}
                              onClick={() => runAction(
                                () => decideTenderKnowledge(leadId, entry.id, 'REJECTED'),
                                'Vidensforslaget er afvist'
                              )}
                            >
                              Afvis
                            </Button>
                          </Space>
                        ) : null}
                      </Space>
                    </List.Item>
                  )}
                />
              </section>
            </>
          ) : null}
        </Space>
      )}

      <Modal
        title={editingField?.label || 'Redigér felt'}
        open={Boolean(editingField)}
        onCancel={() => setEditingField(null)}
        onOk={saveField}
        confirmLoading={actionLoading}
        okButtonProps={{
          disabled: !editingField || fieldDraft.trim() === displayValue(editingField.value).trim()
        }}
        okText="Gem"
        cancelText="Annullér"
      >
        {(editingField?.metadata?.allowed_values || []).length ? (
          <Select
            value={fieldDraft || undefined}
            placeholder="Vælg værdi"
            style={{ width: '100%' }}
            options={(editingField?.metadata?.allowed_values || []).map((value) => ({ value, label: value }))}
            onChange={setFieldDraft}
          />
        ) : (
          <Input.TextArea
            value={fieldDraft}
            autoSize={{ minRows: 3, maxRows: 8 }}
            onChange={(event) => setFieldDraft(event.target.value)}
          />
        )}
      </Modal>

      <Modal
        title={sourceField ? `Kilder · ${sourceField.label}` : 'Kilder'}
        open={Boolean(sourceField)}
        footer={null}
        onCancel={() => setSourceField(null)}
      >
        <List
          dataSource={sourceField?.sources || []}
          locale={{ emptyText: 'Ingen kilder' }}
          renderItem={(source) => (
            <List.Item key={source.id}>
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <Space size={8} wrap>
                  <Tag>{source.source_kind}</Tag>
                  {source.page_number ? <Text type="secondary">Side {source.page_number}</Text> : null}
                  {source.confidence !== null ? (
                    <Text type="secondary">Confidence {Number(source.confidence).toFixed(2)}</Text>
                  ) : null}
                </Space>
                <Paragraph style={{ marginBottom: 0 }}>{source.quote || 'Ingen tekstkilde gemt'}</Paragraph>
              </Space>
            </List.Item>
          )}
        />
      </Modal>
    </Card>
  );
}